import React, { useState } from 'react';
import { Expenditure } from '../types';
import { 
  Plus, 
  Search, 
  Wallet, 
  Calendar, 
  Tag, 
  Trash2, 
  X, 
  AlertCircle, 
  Filter,
  ArrowDownCircle,
  Settings2
} from 'lucide-react';

interface ExpenditureProps {
  expenditures: Expenditure[];
  categories: string[];
  onAddExpenditure: (exp: Omit<Expenditure, 'id'>) => void;
  onDeleteExpenditure: (id: string) => void;
  onAddCategory: (category: string) => void;
  onDeleteCategory: (category: string) => void;
}

const ExpenditureModule: React.FC<ExpenditureProps> = ({ 
  expenditures, 
  categories,
  onAddExpenditure, 
  onDeleteExpenditure,
  onAddCategory,
  onDeleteCategory
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  const [formData, setFormData] = useState<Omit<Expenditure, 'id'>>({
    date: new Date().toISOString().split('T')[0],
    category: categories[0] || 'Other',
    amount: 0,
    description: ''
  });

  const filteredExpenses = expenditures.filter(exp => {
    const matchesSearch = exp.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'All' || exp.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const totalSpent = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAddExpenditure(formData);
    setIsModalOpen(false);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category: categories[0] || 'Other',
      amount: 0,
      description: ''
    });
  };

  const handleAddCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCategoryName.trim() && !categories.includes(newCategoryName.trim())) {
      onAddCategory(newCategoryName.trim());
      setNewCategoryName('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
           <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-red-50 text-red-600 rounded-xl">
                 <ArrowDownCircle size={24} />
              </div>
              <div>
                 <p className="text-sm font-medium text-slate-500 uppercase">Total Expenditure</p>
                 <h3 className="text-2xl font-bold text-red-600">RS. {totalSpent.toLocaleString()}</h3>
              </div>
           </div>
           <div className="flex items-center gap-2 text-xs font-medium text-emerald-600">
              <span className="px-2 py-0.5 bg-emerald-50 rounded-full">Filtered View</span>
           </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
           <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                 <Wallet size={24} />
              </div>
              <div>
                 <p className="text-sm font-medium text-slate-500 uppercase">Transactions</p>
                 <h3 className="text-2xl font-bold text-slate-900">{filteredExpenses.length}</h3>
              </div>
           </div>
           <p className="text-xs text-slate-400">Total recorded entries</p>
        </div>
      </div>

      {/* Main Controls & Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex flex-col lg:flex-row justify-between items-center gap-4">
          <div className="flex w-full lg:w-auto gap-3">
             <div className="relative flex-1 lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="text" 
                  placeholder="Search description..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm transition-all"
                />
             </div>
             <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select 
                  className="pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm transition-all appearance-none"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="All">All Categories</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
             </div>
          </div>
          <div className="flex gap-3 w-full lg:w-auto">
            <button 
              onClick={() => setIsCategoryModalOpen(true)}
              className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold border border-slate-200 hover:bg-slate-50 text-slate-700 transition-all"
            >
              <Settings2 size={18} />
              Categories
            </button>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="flex-[2] lg:flex-none flex items-center justify-center gap-2 bg-[#0f172a] hover:bg-slate-800 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-slate-900/10"
            >
              <Plus size={18} />
              Add Expense
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 text-slate-400 text-[11px] font-bold uppercase tracking-[0.1em]">
                <th className="p-6 border-b border-slate-100">Date</th>
                <th className="p-6 border-b border-slate-100">Category</th>
                <th className="p-6 border-b border-slate-100">Description</th>
                <th className="p-6 border-b border-slate-100">Amount</th>
                <th className="p-6 border-b border-slate-100 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredExpenses.map(exp => (
                <tr key={exp.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-6">
                    <div className="flex items-center gap-3 text-slate-900 font-medium">
                       <Calendar size={14} className="text-slate-400" />
                       {exp.date}
                    </div>
                  </td>
                  <td className="p-6">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold uppercase">
                       <Tag size={12} />
                       {exp.category}
                    </span>
                  </td>
                  <td className="p-6">
                    <p className="text-sm text-slate-600 max-w-xs truncate" title={exp.description}>
                      {exp.description}
                    </p>
                  </td>
                  <td className="p-6 font-bold text-red-600">
                    RS. {exp.amount.toLocaleString()}
                  </td>
                  <td className="p-6 text-right">
                    <button 
                      onClick={() => onDeleteExpenditure(exp.id)}
                      className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredExpenses.length === 0 && (
             <div className="p-20 text-center flex flex-col items-center">
               <div className="bg-slate-100 p-4 rounded-full mb-4">
                 <AlertCircle size={40} className="text-slate-400" />
               </div>
               <p className="text-slate-500 font-medium">No expenses found matching your criteria.</p>
             </div>
          )}
        </div>
      </div>

      {/* Category Management Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
               <h3 className="font-bold text-xl text-slate-900 uppercase">Manage Categories</h3>
               <button onClick={() => setIsCategoryModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                 <X size={24} />
               </button>
            </div>
            <div className="p-8 space-y-6">
              <form onSubmit={handleAddCategorySubmit} className="flex gap-2">
                <input 
                  type="text" 
                  required
                  placeholder="New category name..."
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                />
                <button 
                  type="submit"
                  className="px-4 py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all shadow-md active:scale-95"
                >
                  Add
                </button>
              </form>
              
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-2">Existing Categories</label>
                {categories.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl group border border-transparent hover:border-slate-200 hover:bg-white transition-all">
                    <span className="text-sm font-bold text-slate-700">{cat}</span>
                    <button 
                      onClick={() => onDeleteCategory(cat)}
                      className="text-slate-300 hover:text-red-500 transition-colors"
                      title="Delete category"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className="p-6 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                 <h3 className="font-bold text-xl text-slate-900 uppercase">New Expenditure</h3>
                 <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                   <X size={24} />
                 </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                   <div className="space-y-2">
                      <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Date</label>
                      <input 
                        type="date"
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm"
                        value={formData.date}
                        onChange={e => setFormData({...formData, date: e.target.value})}
                      />
                   </div>
                   <div className="space-y-2">
                      <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Category</label>
                      <select 
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm appearance-none"
                        value={formData.category}
                        onChange={e => setFormData({...formData, category: e.target.value})}
                      >
                         {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                   </div>
                </div>

                <div className="space-y-2">
                   <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Amount (RS.)</label>
                   <input 
                     type="number"
                     required
                     min="0.01"
                     step="0.01"
                     placeholder="0.00"
                     className="w-full px-4 py-3 bg-slate-50 border border-red-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 text-lg font-bold text-red-600"
                     value={formData.amount}
                     onChange={e => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                   />
                </div>

                <div className="space-y-2">
                   <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Description</label>
                   <textarea 
                     required
                     placeholder="What was this expense for?"
                     className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm resize-none"
                     rows={3}
                     value={formData.description}
                     onChange={e => setFormData({...formData, description: e.target.value})}
                   />
                </div>

                <button 
                  type="submit"
                  className="w-full py-4 bg-[#0f172a] hover:bg-slate-800 text-white rounded-2xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-xl shadow-slate-900/20 mt-4 uppercase"
                >
                  Record Expenditure
                </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default ExpenditureModule;